class NostoReact extends HTMLElement {
	// Singleton observers pour éviter les duplications
	static _lazyLoadObserver = null;
	static _isObserverInitialized = false;

	constructor() {
		super();

		// Timeout avant de considérer Nosto comme "en retard" (mais on continue à observer)
		this.NOSTO_TIMEOUT = 3000;

		this.selectors = {
			// Containers
			nostoContent: "#nosto-content",
			nativeCollection: "#collection-native",
			collectionData: ".collection-nosto-data",
			searchData: ".search-nosto-data",
			pushData: ".collection-push-data",
			// Nosto specific
			sidebarMobileToggle: ".ns-facet-sidebar-toggle-label",
			// Search drawer (simplifié)
			nostoSearchDrawer: "#nosto-search-drawer-results",
			searchDrawer: "#search-drawer",
			searchDrawerResults: "#search-drawer-results",
			searchDrawerSkeleton: "#search-drawer-skeleton",
		};

		// Détecter le contexte
		this.context = this.getAttribute("data-context") || "collection";
		this._hasShownNosto = false;
		this._nostoTimedOut = false;
		this._observer = null;
	}

	connectedCallback() {
		if (!window.nostoEnabled) return;
		this._setupContext();
	}

	disconnectedCallback() {
		// Cleanup observer on disconnect
		if (this._observer) {
			this._observer.disconnect();
			this._observer = null;
		}
	}

	async _setupContext() {
		if (this.context === "search-drawer") {
			this._setupSearchDrawer();
			return;
		}

		// Progressive Enhancement: la collection native est déjà visible
		// On observe pour Nosto en parallèle, sans bloquer
		this._setupMainObserver();
	}

	/**
	 * Observer simplifié - le swap visuel est géré par CSS via ax-nosto-loaded
	 * Flow:
	 * 1. Skeleton visible par défaut
	 * 2. Attendre Nosto (ax-nosto-loaded) → CSS affiche Nosto, cache skeleton
	 * 3. Si timeout 5s → cacher skeleton, afficher native (continuer à observer)
	 * 4. Si Nosto arrive en retard → CSS cache native, affiche Nosto
	 */
	_setupMainObserver() {
		const targetNode = this.querySelector(this.selectors.nostoContent);
		if (!targetNode) return;

		const skeleton = this.querySelector("#collection-skeleton, .search__skeleton");
		const nativeCollection = this.querySelector(this.selectors.nativeCollection);

		// Observer pour détecter ax-nosto-loaded (ajouté par ContentWrapper)
		this._observer = new MutationObserver(() => {
			if (targetNode.classList.contains("ax-nosto-loaded") && !this._hasShownNosto) {
				this._onNostoReady();
			}
		});

		this._observer.observe(targetNode, {
			attributes: true,
			attributeFilter: ["class"],
		});

		// Vérifier immédiatement
		if (targetNode.classList.contains("ax-nosto-loaded")) {
			this._onNostoReady();
			return;
		}

		// Timeout: si Nosto trop lent, fallback sur native (collection uniquement)
		if (nativeCollection && this.context !== "search") {
			setTimeout(() => {
				if (!this._hasShownNosto) {
					// Cacher le skeleton
					if (skeleton) {
						skeleton.style.display = "none";
					}
					// Afficher la native
					nativeCollection.classList.add("show-native");
				}
			}, 5000); // 5s timeout
		}
	}

	/**
	 * Appelé quand Nosto est prêt (ax-nosto-loaded détecté)
	 * Le swap visuel est déjà fait par CSS
	 */
	_onNostoReady() {
		if (this._hasShownNosto) return;
		this._hasShownNosto = true;
		this._observer?.disconnect();

		// Cacher la collection native (pour le contexte collection)
		const nativeCollection = this.querySelector(this.selectors.nativeCollection);
		if (nativeCollection) {
			nativeCollection.classList.add("nosto-hidden");
		}

		// Dispatcher les événements
		document.body.style.overflow = "";
		document.dispatchEvent(new CustomEvent("wishlist:update", { bubbles: true }));
		document.dispatchEvent(new CustomEvent("nosto:ready", { bubbles: true }));

		this.handleNostoFilters();
		NostoReact.initLazyLoadObserver();
	}

	_setupSearchDrawer() {
		const nostoSearchDrawer = document.querySelector(this.selectors.nostoSearchDrawer);
		const searchDrawerResults = document.querySelector(this.selectors.searchDrawerResults);

		if (!nostoSearchDrawer || !searchDrawerResults) return;

		this._observer = new MutationObserver(() => {
			if (nostoSearchDrawer.children.length > 0) {
				// Move content
				while (nostoSearchDrawer.firstChild) {
					searchDrawerResults.appendChild(nostoSearchDrawer.firstChild);
				}
				document.dispatchEvent(new CustomEvent("nosto:search-drawer:ready", {
					bubbles: true,
					detail: { timestamp: Date.now() },
				}));
			}
		});

		this._observer.observe(nostoSearchDrawer, { childList: true });

		// Cleanup après 10 secondes (réduit de 30s)
		setTimeout(() => this._observer?.disconnect(), 10000);
	}

	static renderPageTemplate(template = "main-collection") {
		// Utiliser window.Shopify.routes.root pour les URLs localisées
		const root = window.Shopify?.routes?.root || "/";
		let url = `${window.location.pathname}?section_id=${template}`;

		if (template === "main-search") {
			const searchParams = new URLSearchParams(window.location.search);
			const query = searchParams.get("q");
			if (query) {
				url += `&q=${query}`;
			}
		}

		NostoReact.renderCollectionFromFetch(url, template);
	}

	static renderCollectionFromFetch(url, template = "main-collection") {
		fetch(url)
			.then((response) => {
				if (!response.ok) {
					throw new Error(
						`Section fetch failed: ${response.status} ${response.statusText}`,
					);
				}
				return response.text();
			})
			.then((responseText) => {
				// Pour le fallback, on remplace tout le contenu avec le HTML de la section
				NostoReact.renderFallbackContent(responseText, template);
			})
			.catch((error) => {
				console.error("Erreur lors du fetch de la section:", error);
			});
	}

	/**
	 * Fallback: utilisé uniquement pour la page recherche si besoin
	 * Pour les collections, la native est déjà visible
	 */
	static renderFallbackContent(html, template) {
		const parser = new DOMParser();
		const doc = parser.parseFromString(html, "text/html");
		const newCollection = doc.querySelector("#collection");

		if (!newCollection) {
			console.error("[NostoReact] Fallback: Could not find #collection in fetched HTML");
			return;
		}

		const existingCollection = document.querySelector("#collection");
		if (!existingCollection) return;

		const newFacetsDrawer = doc.querySelector("facets-drawer");
		const wrapperId = `shopify-section-${template}`;
		const wrapperDiv = document.createElement("div");
		wrapperDiv.id = wrapperId;
		wrapperDiv.className = "shopify-section";

		const importedCollection = document.importNode(newCollection, true);
		wrapperDiv.appendChild(importedCollection);

		// Update facets forms
		wrapperDiv.querySelectorAll("facets-form").forEach((form) => {
			form.setAttribute("section-id", template);
		});
		wrapperDiv.querySelectorAll("facet-link a").forEach((link) => {
			const href = link.getAttribute("href");
			if (href && !href.includes("#collection")) {
				link.setAttribute("href", href + "#collection");
			}
		});

		existingCollection.innerHTML = "";
		existingCollection.appendChild(wrapperDiv);

		// Update facets drawer if present
		if (newFacetsDrawer) {
			const importedFacetsDrawer = document.importNode(newFacetsDrawer, true);
			importedFacetsDrawer.querySelectorAll("facets-form").forEach((form) => {
				form.setAttribute("section-id", template);
			});

			document.querySelector("facets-drawer")?.remove();
			document.body.appendChild(importedFacetsDrawer);
		}

		requestAnimationFrame(() => {
			NostoReact.initLazyLoadObserver();
			document.dispatchEvent(new CustomEvent("wishlist:update", { bubbles: true }));
			document.dispatchEvent(new CustomEvent("nosto:fallback:ready", { bubbles: true }));
		});
	}

	static initLazyLoadObserver() {
		// Déconnecter l'ancien observer s'il existe
		if (NostoReact.lazyLoadObserver) {
			NostoReact.lazyLoadObserver.disconnect();
		}

		// Fonction pour activer les images d'une card ou d'un push collection
		const activateCardImages = (card) => {
			const images = card.querySelectorAll("img[data-src], img[data-srcset]");
			const videos = card.querySelectorAll("video[data-src]");

			images.forEach((img) => {
				// Convertir data-src en src
				if (img.hasAttribute("data-src")) {
					img.src = img.getAttribute("data-src");
					img.removeAttribute("data-src");
				}

				// Convertir data-srcset en srcset
				if (img.hasAttribute("data-srcset")) {
					img.srcset = img.getAttribute("data-srcset");
					img.removeAttribute("data-srcset");
				}
			});

			videos.forEach((video) => {
				// Convertir data-src en src pour les vidéos et ajouter la source
				if (video.hasAttribute("data-src")) {
					const videoUrl = video.getAttribute("data-src");
					video.removeAttribute("data-src");

					// Mettre à jour la source existante ou en créer une
					let source = video.querySelector("source");
					if (source) {
						source.src = videoUrl;
					} else {
						source = document.createElement("source");
						source.src = videoUrl;
						source.type = "video/mp4";
						video.appendChild(source);
					}

					// Activer l'autoplay et démarrer la vidéo
					video.autoPlay = true;
					video.muted = true;
					video.load();
					video.play();
				}
			});
		};

		NostoReact.lazyLoadObserver = new IntersectionObserver(
			(entries) => {
				entries.forEach((entry) => {
					if (entry.isIntersecting) {
						activateCardImages(entry.target);
						NostoReact.lazyLoadObserver.unobserve(entry.target);
					}
				});
			},
			{
				rootMargin: "50px 0px",
				threshold: 0.1,
			},
		);

		const pushCollections = document.querySelectorAll(".collection-push");

		pushCollections.forEach((push) => {
			NostoReact.lazyLoadObserver.observe(push);
		});
	}

	static renderProductGridContainer(html) {
		const nostoContent = document.querySelector("#nosto-content");
		const nativeCollection = document.querySelector("#collection-native");
		if (!nostoContent) return;

		const parsedHtml = new DOMParser().parseFromString(html, "text/html");
		const productGridContainer = parsedHtml.getElementById("ProductGridContainer");

		if (productGridContainer) {
			nostoContent.innerHTML = productGridContainer.innerHTML;
		}

		// Cacher la collection native
		if (nativeCollection) {
			nativeCollection.classList.add("nosto-hidden");
		}

		// Afficher le contenu Nosto
		nostoContent.classList.remove("nosto-hidden");
		nostoContent.classList.add("nosto-visible");

		// Annuler les animations scroll-trigger
		nostoContent.querySelectorAll(".scroll-trigger").forEach((element) => {
			element.classList.add("scroll-trigger--cancel");
		});

		// Initialiser l'observer pour les images lazy load
		NostoReact.initLazyLoadObserver();
		document.dispatchEvent(new CustomEvent("wishlist:update", { bubbles: true }));
		document.dispatchEvent(new CustomEvent("nosto:ready", { bubbles: true }));
	}

	handleEmptyState() {
		const nostoContent = this.querySelector(this.selectors.nostoContent);
		const nativeCollection = this.querySelector(this.selectors.nativeCollection);
		const skeleton = this.querySelector(".search__skeleton, .collection__skeleton");
		const floatingButton = this.querySelector(".collection__floating-button-skeleton");

		// Cacher la native ou le skeleton
		if (nativeCollection) {
			nativeCollection.classList.add('nosto-hidden');
		}
		if (skeleton) {
			skeleton.remove();
		}
		if (floatingButton) {
			floatingButton.remove();
		}

		// Afficher le message "Pas de résultat"
		if (nostoContent) {
			nostoContent.classList.remove('nosto-hidden');
			nostoContent.classList.add('nosto-visible');

			const noResultsText = this.getAttribute('data-no-results-text') || 'Aucun résultat';
			const noResultsSubtext = this.getAttribute('data-no-results-subtext') || '';

			nostoContent.innerHTML = `
				<div class="empty-state text-center py-12">
					<p class="h4">${noResultsText}</p>
					<p>${noResultsSubtext}</p>
				</div>
			`;
		}

		document.body.style.overflow = '';
		document.dispatchEvent(new CustomEvent('nosto:ready', { bubbles: true }));
	}

	/**
	 * Cache la collection native et affiche Nosto
	 */
	showNostoContent() {
		const nostoContent = this.querySelector(this.selectors.nostoContent);
		const nativeCollection = this.querySelector(this.selectors.nativeCollection);

		if (nativeCollection) {
			nativeCollection.classList.add("nosto-hidden");
		}

		if (nostoContent) {
			nostoContent.classList.remove("nosto-hidden");
			nostoContent.classList.add("nosto-visible");
		}
	}


	handleNostoFilters() {
		// Ajouter un style pour cacher les filtres pendant le chargement
		const style = document.createElement("style");
		style.textContent = `
			.ns-sidebar-dropdown {
				visibility: hidden;
			}
		`;
		document.head.appendChild(style);

		const checkAndUpdateFilters = () => {
			// Attendre que Nosto soit complètement chargé
			if (!window.nostojs || !window.nostoTemplatesLoaded) {
				setTimeout(checkAndUpdateFilters, 100);
				return;
			}

			// Attendre que les filtres soient dans le DOM
			const filtersList = document.querySelectorAll(".ns-sidebar-dropdown");
			if (!filtersList || filtersList.length === 0) {
				setTimeout(checkAndUpdateFilters, 100);
				return;
			}

			// Fermer le premier filtre
			const targetFilter = filtersList[0];
			if (targetFilter) {
				targetFilter.classList.remove("ns-active");

				// Rendre les filtres visibles une fois le premier fermé
				setTimeout(() => {
					style.textContent = `
						.ns-sidebar-dropdown {
							visibility: visible;
						}
					`;
				}, 50);
			}
		};

		// Commencer la vérification immédiatement
		checkAndUpdateFilters();
	}
}

customElements.get("nosto-react") ||
	customElements.define("nosto-react", NostoReact);

// ===========================================
// PROTECTION POUR PRICE-RANGE
// Patch PriceRange pour éviter les erreurs si les éléments ne sont pas présents
// ===========================================
(function () {
	const patchPriceRange = () => {
		const PriceRangeClass = window.customElements.get("price-range");
		if (!PriceRangeClass || PriceRangeClass._patched) return;

		const originalConnectedCallback =
			PriceRangeClass.prototype.connectedCallback;

		PriceRangeClass.prototype.connectedCallback = function () {
			try {
				// Vérifier que tous les éléments requis existent
				const rangeLowerBound = this.querySelector(
					'input[type="range"]:first-child',
				);
				const rangeHigherBound = this.querySelector(
					'input[type="range"]:last-child',
				);
				const textInputLowerBound = this.querySelector(
					'input[name="filter.v.price.gte"]',
				);
				const textInputHigherBound = this.querySelector(
					'input[name="filter.v.price.lte"]',
				);

				if (
					rangeLowerBound &&
					rangeHigherBound &&
					textInputLowerBound &&
					textInputHigherBound
				) {
					originalConnectedCallback.call(this);
				} else {
					// Réessayer après un délai
					setTimeout(() => {
						const stillHasElements =
							this.querySelector('input[type="range"]:first-child') &&
							this.querySelector('input[type="range"]:last-child') &&
							this.querySelector('input[name="filter.v.price.gte"]') &&
							this.querySelector('input[name="filter.v.price.lte"]');

						if (stillHasElements) {
							try {
								originalConnectedCallback.call(this);
							} catch (e) {
								// Silencieux - l'élément n'a probablement pas de filtres de prix
							}
						}
					}, 50);
				}
			} catch (error) {
				// Silencieux - l'erreur vient du fait que l'élément n'a pas de contenu
			}
		};

		PriceRangeClass._patched = true;
	};

	// Essayer immédiatement
	patchPriceRange();

	// Réessayer après un délai au cas où PriceRange n'est pas encore défini
	setTimeout(patchPriceRange, 100);
})();

// ===========================================
// STATIC LOADING LISTENERS (Global scope)
// Ces listeners persistent même après le remplacement du DOM
// ===========================================

(function () {
	let isLoadingListenersSetup = false;

	function showLoadingState() {
		const collection = document.querySelector("#collection");

		if (!collection) {
			return;
		}

		// Stocker la hauteur actuelle pour éviter le CLS
		const currentHeight = collection.offsetHeight;
		collection.style.minHeight = `${currentHeight}px`;

		// Ajouter la classe de loading
		collection.classList.add("is-loading");
		// Réduire l'opacité du contenu pour indiquer le chargement
		const content =
			collection.querySelector(".shopify-section") ||
			collection.querySelector("#nosto-content");
		if (content) {
			content.style.opacity = "0.5";
			content.style.pointerEvents = "none";
			content.style.transition = "opacity 0.2s ease";
		}
	}

	function hideLoadingState() {
		const collection = document.querySelector("#collection");

		if (!collection) {
			return;
		}

		// Retirer la hauteur minimale après un délai pour laisser le nouveau contenu se charger
		setTimeout(() => {
			collection.style.minHeight = "";
		}, 100);

		// Retirer la classe de loading
		collection.classList.remove("is-loading");

		// Restaurer l'opacité du contenu
		const content =
			collection.querySelector(".shopify-section") ||
			collection.querySelector("#nosto-content");
		if (content) {
			content.style.opacity = "";
			content.style.pointerEvents = "";
		}
	}

	// Attacher les listeners une seule fois
	if (!isLoadingListenersSetup) {
		// Écouter sur document.documentElement car c'est là que theme.js dispatch les events
		document.documentElement.addEventListener("theme:loading:start", () => {
			showLoadingState();
		});

		document.documentElement.addEventListener("theme:loading:end", () => {
			// Petit délai pour laisser le DOM se stabiliser après le remplacement
			requestAnimationFrame(() => {
				requestAnimationFrame(() => {
					hideLoadingState();
				});
			});
		});

		// DEBUG: Écouter l'événement facet:update pour voir s'il est dispatché
		document.addEventListener("facet:update", (event) => {
			// Vérifier si l'élément shopify-section existe
			const sectionId = event.detail?.url?.searchParams?.get("section_id");
			const shopifySection = document.getElementById(
				`shopify-section-${sectionId}`,
			);
		});

		isLoadingListenersSetup = true;
	}
})();
